import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Anchor, Globe2, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useT } from "@/i18n/LanguageContext";

const HERO_IMG =
  "https://images.unsplash.com/photo-1673902219551-6e6de00d2e13?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHxjYXJnbyUyMHNoaXAlMjBvY2VhbnxlbnwwfHx8fDE3NzgyODAzMjh8MA&ixlib=rb-4.1.0&q=85";

const STAT_ICONS = [Globe2, Anchor, TrendingUp, null];

export default function Hero() {
  const { t } = useT();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden bg-slate-950"
    >
      {/* Parallax Background */}
      <motion.div
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 will-change-transform"
      >
        <img
          src={HERO_IMG}
          alt={t.hero.title3}
          className="w-full h-[120%] object-cover"
          loading="eager"
        />
        {/* Tinting + gradient overlay for legibility */}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/85 via-slate-950/55 to-slate-950/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-transparent to-slate-950/20" />
      </motion.div>

      {/* Decorative grid lines */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="max-w-7xl mx-auto h-full px-6 lg:px-10 grid grid-cols-12 gap-8">
          {Array.from({ length: 13 }).map((_, i) => (
            <div
              key={i}
              className="border-l border-white/5 h-full"
              style={{ gridColumn: i === 0 ? "1" : `${i + 1}` }}
            />
          ))}
        </div>
      </div>

      {/* Content */}
      <motion.div
        style={{ y: contentY, opacity }}
        className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-40 pb-24 min-h-[100svh] flex flex-col justify-end"
      >
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="flex items-center gap-3 mb-6"
        >
          <div className="h-px w-12 bg-[#FF6B00]" />
          <span className="text-[#FF6B00] text-xs tracking-[0.24em] uppercase font-bold">
            {t.hero.overline}
          </span>
        </motion.div>

        <motion.h1
          data-testid="hero-headline"
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className="font-display text-white text-5xl sm:text-6xl lg:text-7xl xl:text-[88px] font-bold tracking-[-0.03em] leading-[0.98] max-w-5xl"
        >
          {t.hero.title1}<br />
          <span className="text-white/60">{t.hero.title2}</span>{" "}
          <span className="relative inline-block">
            <span className="relative z-10">{t.hero.title3}</span>
            <span className="absolute bottom-2 left-0 right-0 h-3 bg-[#FF6B00] -z-0 opacity-90" />
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.55 }}
          className="mt-8 text-white/75 text-lg md:text-xl max-w-2xl leading-relaxed font-light"
        >
          {t.hero.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.75 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Button
            data-testid="hero-cta-quote"
            asChild
            size="lg"
            className="bg-[#FF6B00] hover:bg-[#e55f00] text-white rounded-full h-14 px-7 text-[15px] font-semibold group shadow-[0_10px_40px_rgba(255,107,0,0.4)]"
          >
            <a href="#contact" className="inline-flex items-center gap-2">
              {t.hero.ctaQuote}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </a>
          </Button>
          <Button
            data-testid="hero-cta-services"
            asChild
            variant="outline"
            size="lg"
            className="bg-white/5 border-white/30 hover:bg-white/15 text-white hover:text-white rounded-full h-14 px-7 text-[15px] font-medium backdrop-blur-md"
          >
            <a href="#services">{t.hero.ctaServices}</a>
          </Button>
        </motion.div>

        {/* Stats row */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1 }}
          className="mt-20 pt-8 border-t border-white/15 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-4xl"
        >
          {t.hero.stats.map((s, i) => {
            const Icon = STAT_ICONS[i];
            return (
              <div key={i} className="text-white">
                <div className="flex items-center gap-2 mb-2 text-[#FF6B00]">
                  {Icon && <Icon className="w-4 h-4" strokeWidth={2.5} />}
                  <span className="text-[10px] tracking-[0.2em] uppercase font-bold opacity-80">
                    {s.label}
                  </span>
                </div>
                <div className="font-display text-3xl md:text-4xl font-bold tracking-tight">
                  {s.num}
                </div>
              </div>
            );
          })}
        </motion.div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
      >
        <span className="text-white/60 text-[10px] tracking-[0.3em] uppercase font-medium">
          {t.hero.scroll}
        </span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
          className="w-px h-10 bg-gradient-to-b from-white/60 to-transparent"
        />
      </motion.div>
    </section>
  );
}
